import { Inject, Injectable, OnDestroy } from '@angular/core';
import { AppKey, GetMultiAppRequest, PartnerApiService, SharedMarketingInformation } from '@vendasta/marketplace-apps';
import { distinctUntilChanged, map, Observable, of, Subscription, switchMap } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { applyItemWhitelabel, GenericItem, getItemKey, getItemKeys, isAddon } from './item-whitelabel';

@Injectable({
  providedIn: 'root',
})
export class ItemWhitelabelService implements OnDestroy {
  private partnerIdSubscription: Subscription;
  private itemWhitelabelMap = new Map<string, Map<string, SharedMarketingInformation>>();

  constructor(
    @Inject('PARTNER_ID') readonly partnerId$: Observable<string>,
    private partnerMarketplaceService: PartnerApiService,
  ) {
    this.partnerIdSubscription = this.partnerId$.pipe(distinctUntilChanged()).subscribe(() => {
      this.itemWhitelabelMap = new Map<string, Map<string, SharedMarketingInformation>>();
    });
  }

  ngOnDestroy(): void {
    this.partnerIdSubscription.unsubscribe();
  }

  public whitelabelItems$<T extends GenericItem>(items: T[], marketID: string): Observable<T[]> {
    return this.loadItemWhitelabelMap$(getItemKeys(items), marketID).pipe(
      map((whitelabelMap) => {
        return items.map((i) => {
          const w = whitelabelMap.get(getItemKey(i).appId);
          if (w) {
            const parentWhitelabel = isAddon(i) ? whitelabelMap.get(i.appId) : null;
            return applyItemWhitelabel<T>(i, w, parentWhitelabel);
          }
          return i;
        });
      }),
    );
  }

  private loadItemWhitelabelMap$(
    appKeys: AppKey[],
    marketID: string,
  ): Observable<Map<string, SharedMarketingInformation>> {
    marketID = marketID ?? 'default';

    // use existing map if all specified items are cached
    const marketCache = this.getMarketCache(marketID);
    if (appKeys.every((appKey) => marketCache.has(appKey.appId))) {
      return of(marketCache);
    }

    return this.partnerId$.pipe(
      switchMap((partnerId) => {
        return this.partnerMarketplaceService.getMultiApp(
          new GetMultiAppRequest({
            partnerId: partnerId,
            marketId: marketID,
            appKeys: appKeys,
            includeNotEnabled: true,
            projectionFilter: {
              paths: ['sharedMarketingInformation'],
            },
          }),
        );
      }),
      map((res) => {
        // cache whitelabel data in a map for faster access and to prevent redundant API calls
        for (const item of res.apps) {
          if (item?.key?.appId) {
            marketCache.set(item.key.appId, item.sharedMarketingInformation);
          }
        }
        return marketCache;
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  private getMarketCache(marketId: string): Map<string, SharedMarketingInformation> {
    let marketCache = this.itemWhitelabelMap.get(marketId);
    if (!marketCache) {
      marketCache = new Map<string, SharedMarketingInformation>();
      this.itemWhitelabelMap.set(marketId, marketCache);
    }
    return marketCache;
  }
}
